// Ретеншен с UTM-разбивкой (`source_request.retention_utm_breakdown`).
//
// Бэкенд отдаёт те же строки ретеншена, что и обычно (одна строка = одна когорта),
// но продублированные на каждую метку и с полями utm_id / utm_name / current_level /
// next_level (см. RetentionQueryService в www). Рисовать их как есть нельзя: и график,
// и таблица ключуются по дате когорты, и строки разных меток за одну дату схлопнулись
// бы друг в друга. Поэтому в этом режиме второе измерение — метка, а когорты
// сворачиваются в одну усреднённую кривую на метку.

export function isRetentionUtmBreakdown(reportData) {
  const sr = reportData?.source_request
  if (!sr?.retention_utm_breakdown) return false
  // Тип берём из source_request в первую очередь: он авторитетнее верхнего
  // уровня конверта (см. reference_report_config_envelope) и присутствует и в
  // глобальном состоянии конструктора, и в локальном состоянии виджета.
  return (sr.type || reportData?.type) === 'retention'
}

/**
 * Ключ строки — путь до метки (`current_level`), а не голый `utm_id`.
 *
 * Имена уровней резолвятся из двух разных словарей (`utm_params` и `utm_value`),
 * поэтому id параметра и id значения могут совпасть — по одному id строки разных
 * уровней склеились бы. Путь уникален всегда. Совпадает с `rowKey` в
 * _utm_report.vue, чтобы дерево ретеншена и UTM-отчёта ключевались одинаково.
 */
export function utmRowKey(row) {
  if (Array.isArray(row?.current_level) && row.current_level.length) {
    return row.current_level.join('_')
  }
  return String(row?.utm_id ?? 0)
}

/**
 * Сворачивает строки-когорты в строки-метки.
 *
 * `mode`:
 *  - `simple` — среднее из процентов когорт (все когорты равны по весу);
 *  - `smart`  — взвешенное по размеру когорты: sum(вернулось) / sum(размер).
 * Это тот же `mean_retention_calculation`, что управляет средней линией обычного
 * ретеншена, — иначе средняя по меткам и общая средняя считались бы по-разному.
 *
 * @returns {Array<{key, utm_id, utm_name, current_level, next_level, size, intervals}>}
 */
export function aggregateRetentionByLabel(rows, { maxIntervals = 8, mode = 'simple' } = {}) {
  if (!Array.isArray(rows) || rows.length === 0) return []

  const groups = new Map()

  rows.forEach(row => {
    const counts = Array.isArray(row?.values) ? row.values.map(v => Number(v?.count) || 0) : null
    if (!counts || counts.length === 0) return

    const key = utmRowKey(row)

    if (!groups.has(key)) {
      groups.set(key, {
        key,
        utm_id: row.utm_id ?? 0,
        utm_name: row.utm_name ?? row.breakdown_value ?? '',
        current_level: Array.isArray(row.current_level) ? [...row.current_level] : [],
        next_level: 0,
        size: 0,
        // Для smart: суммы по интервалам. Для simple: суммы процентов и счётчик когорт.
        sums: new Array(maxIntervals).fill(0),
        percentSums: new Array(maxIntervals).fill(0),
        percentCounts: new Array(maxIntervals).fill(0),
      })
    }

    const group = groups.get(key)
    const cohortSize = counts[0] || 0

    group.size += cohortSize
    group.next_level = Math.max(group.next_level, Number(row.next_level) || 0)

    for (let i = 0; i < maxIntervals; i++) {
      const value = counts[i]
      if (value === undefined) continue

      group.sums[i] += value

      // Пустая когорта не участвует в простом среднем: 0/0 — это «нет данных»,
      // а не нулевое удержание, и иначе она утянула бы кривую вниз.
      if (cohortSize > 0) {
        group.percentSums[i] += (value / cohortSize) * 100
        group.percentCounts[i] += 1
      }
    }
  })

  return Array.from(groups.values()).map(group => {
    const intervals = []

    for (let i = 0; i < maxIntervals; i++) {
      if (mode === 'smart') {
        intervals.push(group.size > 0 ? round1((group.sums[i] / group.size) * 100) : null)
      } else {
        intervals.push(group.percentCounts[i] > 0
          ? round1(group.percentSums[i] / group.percentCounts[i])
          : null)
      }
    }

    return {
      key: group.key,
      utm_id: group.utm_id,
      utm_name: group.utm_name,
      current_level: group.current_level,
      next_level: group.next_level,
      size: group.size,
      intervals,
      // Абсолютные числа вернувшихся по интервалам — процент сам по себе скрывает
      // размер выборки (25% от четырёх человек и от четырёх тысяч выглядят
      // одинаково), поэтому они показываются в подсказке ячейки.
      counts: group.sums.slice(0, maxIntervals),
    }
  }).sort((a, b) => b.size - a.size)
}

function round1(value) {
  return Number(Number(value).toFixed(1))
}
